import generateUniqueID from "@core/utils/generateUniqueID";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import type { CatalogViewDraft } from "@ext/catalog/views/components/Sections/EditCatalogView/EditCatalogView";
import {
	type EditCatalogViewSchema,
	getEditCatalogViewSchema,
} from "@ext/catalog/views/logic/schema/getEditCatalogViewSchema";
import createViewModel from "@ext/catalog/views/logic/utils/createViewModel";
import isTempView from "@ext/catalog/views/logic/utils/isTempView";
import { HIERARCHY_GROUPING, isHierarchyGrouping, resolveGroupBy } from "@ext/catalog/views/logic/utils/resolveGroupBy";
import type { CatalogView } from "@ext/catalog/views/models/CatalogViews";
import { CATALOG_TEMP_VIEW_ID } from "@ext/catalog/views/models/consts";
import t from "@ext/localization/locale/translate";
import type { PropertyID } from "@ext/properties/models";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

export type CatalogViewState = "idle" | "editing" | "saving";

const emptyDraft: EditCatalogViewSchema = {
	name: "",
	filters: [],
	properties: [],
	groupBy: HIERARCHY_GROUPING,
	options: {},
};

const draftOf = (view: CatalogView): EditCatalogViewSchema =>
	isTempView(view)
		? {
				...emptyDraft,
				filters: view.filters ?? [],
				properties: view.properties ?? [],
				groupBy: resolveGroupBy(view.groupBy),
			}
		: emptyDraft;

const useDraftForOpenCatalog = (form: UseFormReturn<EditCatalogViewSchema>) => {
	const { catalogName, resolvedView } = useCatalogPropsStore(
		(state) => ({ catalogName: state.data?.name, resolvedView: state.data?.resolvedView }),
		"shallow",
	);
	const drafted = useRef<string>(null);

	useEffect(() => {
		if (!catalogName || drafted.current === catalogName) return;
		drafted.current = catalogName;
		form.reset(draftOf(resolvedView));
	}, [catalogName, resolvedView, form]);
};

interface UseCatalogViewSettingsProps {
	items: CatalogView[];
	onSaveClick?: (view: CatalogView) => Promise<void> | void;
	onUpdateClick?: (view: CatalogView) => Promise<void> | void;
}

export const useCatalogViewSettings = ({ items, onSaveClick, onUpdateClick }: UseCatalogViewSettingsProps) => {
	const [state, setState] = useState<CatalogViewState>("idle");
	const [editingId, setEditingId] = useState<string>(null);

	const form = useForm<EditCatalogViewSchema>({
		resolver: zodResolver(getEditCatalogViewSchema()),
		defaultValues: emptyDraft,
	});

	useDraftForOpenCatalog(form);

	const { call: applyTempView } = useDeferApi({
		onDone: () => {
			refreshPage();
		},
	});

	const applyTempViewHandler = useCallback(
		(name: string, draft: CatalogViewDraft) => {
			const { filters, properties, groupBy } = draft;
			const isEmptyDraft = !filters.length && !properties.length && isHierarchyGrouping(groupBy);
			const body = isEmptyDraft
				? null
				: createViewModel({
						id: CATALOG_TEMP_VIEW_ID,
						name,
						filters,
						properties,
						groupBy,
						options: { temp: true },
					});

			void applyTempView({ url: (api) => api.applyTempCatalogView(), opts: { body } });
		},
		[applyTempView],
	);

	const exitFromViewHandler = useCallback(() => {
		void applyTempView({
			url: (api) => api.applyTempCatalogView(),
			opts: { body: null },
		});
	}, [applyTempView]);

	const reset = useCallback(() => {
		form.reset(emptyDraft);
		exitFromViewHandler();
		setEditingId(null);
		setState("idle");
	}, [form, exitFromViewHandler]);

	const onChange = useCallback(
		(draft: CatalogViewDraft) => {
			form.setValue("filters", draft.filters);
			form.setValue("properties", draft.properties);
			form.setValue("groupBy", draft.groupBy);
			const name = form.getValues("name") || t("catalog.views.temp-view");
			applyTempViewHandler(name, draft);
		},
		[form, applyTempViewHandler],
	);

	const onEditClick = useCallback(
		(view: CatalogView) => {
			setState("editing");
			setEditingId(view.id);
			const groupBy = resolveGroupBy(view.groupBy);
			form.reset({
				name: view.name,
				filters: view.filters,
				properties: view.properties,
				groupBy,
				options: view.options ?? {},
			});
			applyTempViewHandler(view.name, { filters: view.filters, properties: view.properties, groupBy });
		},
		[form, applyTempViewHandler],
	);

	const onStartSaving = useCallback(() => {
		setState("saving");
	}, []);

	const onSubmit = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			await form.handleSubmit(async (data) => {
				if (state === "saving") {
					await onSaveClick?.({
						id: generateUniqueID(),
						name: data.name,
						filters: data.filters,
						properties: data.properties,
						groupBy: resolveGroupBy(data.groupBy as PropertyID[]),
						options: data.options,
					} as CatalogView);
				} else if (state === "editing") {
					const editingView = items.find((v) => v.id === editingId);
					if (editingView)
						await onUpdateClick?.({
							...editingView,
							name: data.name,
							filters: data.filters,
							properties: data.properties,
							groupBy: resolveGroupBy(data.groupBy as PropertyID[]),
							options: data.options,
						} as CatalogView);
				}
				reset();
			})(e);
		},
		[form, state, editingId, items, onSaveClick, onUpdateClick, reset],
	);

	return {
		form,
		state,
		editingId,
		isEditingOrSaving: state === "editing" || state === "saving",
		onChange,
		onEditClick,
		onStartSaving,
		onSubmit,
		onCancel: reset,
	};
};
